import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useAuth } from "hooks/useAuth";
import BoxContent from "shared/ui/BoxContent";
import PageTitle from "shared/ui/PageTitle";
import Loading from "shared/ui/Loading";
import FormField from "shared/form/FormField";
import ErrorsList from "shared/ui/ErrorsList";
import FormLabelBox from "shared/ui/FormLabelBox";
import FormFieldBox from "shared/ui/FormFieldBox";
import GenericButton from "shared/ui/GenericButton";
import CustomButton from "shared/ui/CustomButton";
import {APICheckStudents, APIGetStudent} from "features/users/api/student";
import { APIUpdateStudent, APIPostStudent } from "features/users/api/student";
import { useLocation } from "react-router-dom";
import {getRomanNumber, niceUserName, useRouteMatch} from "utils/functions";
import DashboardStatistics from "../../dashboard/components/DashboardStatistics";

import StudentWorkPlan from "../../workPlan/components/StudentWorkPlan";
import TestsHistory from "../../tests/listing/components/TestsHistory";
import {useServerErrors} from "../../../hooks/useServerErrors";

interface IFormStudentEdit {
    last_name: string;
    first_name: string;
    email: string;
    gender: string;
    status: number;
    classrooms: any[];
    password?: string;
    confirm_password?: string;
}

const schema = yup
    .object({
    })
    .required();

const Student = () => {
    const { id = "", action } = useParams();
    const { update, tenant, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const currentTab = useRouteMatch([`/elevi/${id}`, `/elevi/${id}/plan-lucru`, `/elevi/${id}/teste`]);
    const allowedTabs = [`/elevi/${id}`, `/elevi/${id}/plan-lucru`, `/elevi/${id}/teste`,];
    const [loadingStudent, setLoadingStudent] = useState<boolean>(true);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [student, setStudent] = useState<IFormStudentEdit>({
        last_name: "",
        first_name: "",
        email: "",
        gender: "",
        status: 1,
        classrooms: [],
    });
    const [studentName, setStudentName] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [studentsCheck, setStudentsCheck] = useState("");
    const methods = useForm<IFormStudentEdit>({
        resolver: yupResolver(schema),
        shouldUnregister: true,
    });
    const { handleServerErrors } = useServerErrors<IFormStudentEdit>(methods.setError);

    useEffect(() => {
        setLoadingStudent(true);

        if (id !== "") {
            APIGetStudent({ id: id })
                .then((response) => {
                    if (response.data.success) {
                        // console.log("getStudent OK", response);
                        if (response.data.data !== null) {
                            setStudent(response.data.data.item);
                            setStudentName(niceUserName(response.data.data.item.first_name, response.data.data.item.last_name, response.data.data.item.email));
                        } else navigate("/");
                        setLoadingStudent(false);
                    } else update("error", { show: true, code: "A002" });
                })
                .catch(() => update("error", { show: true, code: "A001" }));
        } else {
            let locationArray = location.pathname.split("/");
            if (locationArray[locationArray.length - 1] === "adauga") {
                APICheckStudents()
                    .then((response) => {
                        if(response.data.success) {
                            setEditMode(true);
                            setLoadingStudent(false);
                        } else update("error", { show: true, code: "A002" });
                    })
                    .catch((err) => {
                        if (err.response.status === 400) {
                            let errors = err.response.data.data;
                            setStudentsCheck(errors.values[0]);
                            setEditMode(true);
                            setLoadingStudent(false);
                        } else update("error", { show: true, code: "A001" });
                    })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (id !== "") {
            methods.clearErrors();
            setErrorMessage("");
            setEditMode(action === "editare");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, id]);

    const handleSubmitError = (err: any) => {
        if (err.response.status === 400) {
            handleServerErrors(err.response.data.data, {});
            update("loading", {show: false});
        } else {
            update("error", {show: true, code: "A001"});
        }
    };

    const onSubmit: SubmitHandler<IFormStudentEdit> = (data) => {
        if (
            data.last_name !== student.last_name ||
            data.first_name !== student.first_name ||
            data.email !== student.email ||
            data.gender !== student.gender || data.password !== undefined || data.confirm_password !== undefined
        ) {
            methods.clearErrors();
            setErrorMessage("");
            update("loading", { show: true, modal: true });
            if (id !== "") {
                APIUpdateStudent(id, {
                    last_name: data.last_name,
                    first_name: data.first_name,
                    email: data.email,
                    gender: data.gender,
                    status: student.status,
                    password: data.password,
                    confirm_password: data.confirm_password,
                })
                    .then((response) => {
                        if (response.data.success) {
                            // console.log("updateStudent OK", response);
                            setStudent(prevState => ({...prevState, last_name: data.last_name, first_name: data.first_name, email: data.email, gender: data.gender}));
                            setStudentName(niceUserName(data.first_name, data.last_name, data.email));
                            setEditMode(false);
                            navigate(-1);
                        } else {
                            update("error", { show: true, code: "A002" });
                        }
                    })
                    .catch(handleSubmitError)
                    .finally(() => update("loading", { show: false }))
            } else {
                APIPostStudent({
                    last_name: data.last_name,
                    first_name: data.first_name,
                    email: data.email,
                    gender: data.gender,
                    status: 1,
                    password: data.password,
                    confirm_password: data.confirm_password,
                })
                    .then((response) => {
                        if (response.data.success) {
                            // console.log("postStudent OK", response);
                            setStudent(data);
                            setStudentName(niceUserName(data.first_name, data.last_name, data.email));
                            setEditMode(false);
                            navigate(`/elevi/${response.data.data.item.id}`, { replace: true });
                        } else {
                            update("error", { show: true, code: "A002" });
                        }
                    })
                    .catch(handleSubmitError)
                    .finally(() => update("loading", { show: false }))
            }
        } else {
            setErrorMessage("Nu ați modificat niciun câmp.");
        }
    };

    return (
        <Box component="main">
            {loadingStudent ? (
                <Loading show={true} />
            ) : (
                <>
                    <PageTitle
                        back={true}
                        actions={
                            editMode ? (
                                <GenericButton type="close" onClick={() => navigate(-1)}/>
                            ) : ((user.position === "principal" || tenant.type === "teacher") &&
                                <GenericButton type="edit" onClick={() => navigate(`/elevi/${id}/editare`)}/>
                            )
                        }
                    >
                        {id !== "" ? (
                            <>Elev <strong style={{ fontWeight: 400 }}>{studentName}</strong></>
                        ) : (
                            <>Adaugă elev</>
                        )}
                    </PageTitle>

                    <BoxContent>
                        <Box width="100%">
                            {studentsCheck && editMode && id === "" ? (
                                <Typography>{studentsCheck}</Typography>
                            ) : (
                                <FormProvider {...methods}>
                                    <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                                        <Grid container spacing={1.5} alignItems="center">

                                            <FormLabelBox title="Nume:" />
                                            <FormFieldBox>
                                                {!editMode ? (
                                                    student.last_name
                                                ) : (
                                                    <FormField
                                                        name="last_name"
                                                        type="text"
                                                        defaultValue={student.last_name}
                                                        label="Nume"
                                                        autoFocus={true}
                                                        error={!!methods.formState.errors.last_name?.message}
                                                        errorMessage={methods.formState.errors.last_name?.message}
                                                    />
                                                )}
                                            </FormFieldBox>

                                            <FormLabelBox title="Prenume:" />
                                            <FormFieldBox>
                                                {!editMode ? (
                                                    student.first_name
                                                ) : (
                                                    <FormField
                                                        error={!!methods.formState.errors.first_name?.message}
                                                        name="first_name"
                                                        type="text"
                                                        defaultValue={student.first_name}
                                                        label="Prenume"
                                                        errorMessage={methods.formState.errors.first_name?.message}
                                                    />
                                                )}
                                            </FormFieldBox>

                                            <FormLabelBox title="Gen:" />
                                            <FormFieldBox>
                                                {!editMode ? (
                                                    (student.gender && student.gender === "M") ? "Masculin" : (student.gender && student.gender === "F") ? "Feminin" : ""
                                                ) : (
                                                    <FormField
                                                        error={!!methods.formState.errors.gender?.message}
                                                        name="gender"
                                                        type="select"
                                                        defaultValue={student.gender}
                                                        options={[{
                                                            label: "Feminin",
                                                            value: "F",
                                                        }, {
                                                            label: "Masculin",
                                                            value: "M",
                                                        }]}
                                                        label="Gen"
                                                        errorMessage={methods.formState.errors.gender?.message}
                                                    />
                                                )}
                                            </FormFieldBox>

                                            <FormLabelBox title="E-mail:" />
                                            <FormFieldBox>
                                                {!editMode ? (
                                                    student.email
                                                ) : (
                                                    <FormField
                                                        error={!!methods.formState.errors.email?.message}
                                                        name="email"
                                                        type="text"
                                                        defaultValue={student.email}
                                                        label="E-mail"
                                                        errorMessage={methods.formState.errors.email?.message}
                                                        inputProps={{
                                                            autoCapitalize: 'none',
                                                            autoComplete: 'new-email',
                                                        }}
                                                    />
                                                )}
                                            </FormFieldBox>

                                            {editMode && (
                                                <>
                                                    <FormLabelBox title="Parola:" />
                                                    <FormFieldBox>
                                                        <FormField
                                                            name="password"
                                                            type="password"
                                                            label="Parolă"
                                                            autoComplete="new-password"
                                                            error={!!methods.formState.errors.password?.message}
                                                            errorMessage={methods.formState.errors.password?.message}
                                                        />
                                                    </FormFieldBox>

                                                    <FormLabelBox title="Confirmă parola:" />
                                                    <FormFieldBox>
                                                        <FormField
                                                            name="confirm_password"
                                                            type="password"
                                                            label="Confirmă parola"
                                                            autoComplete="new-password"
                                                            error={!!methods.formState.errors.confirm_password?.message}
                                                            errorMessage={methods.formState.errors.confirm_password?.message}
                                                        />
                                                    </FormFieldBox>
                                                </>
                                            )}

                                            {!editMode && (
                                                <>
                                                    <FormLabelBox title="Clasă:" />
                                                    <FormFieldBox>
                                                        {student.classrooms ? (
                                                            <>
                                                                {student.classrooms.map((classroom: any, index: number) => (
                                                                    <Box key={index}>
                                                                        <Link to={`/clase/${classroom.id}`}>({getRomanNumber(classroom.grade)}) {classroom.name}</Link>
                                                                    </Box>
                                                                ))}
                                                            </>
                                                        ) : (
                                                            <Box>Nimic selectat</Box>
                                                        )}
                                                    </FormFieldBox>
                                                </>
                                            )
                                            }

                                            {editMode && (
                                                <>
                                                    <FormLabelBox />
                                                    <FormFieldBox>
                                                        <CustomButton submit buttonType="textIcon" bgcolor="dark">
                                                            {id !== "" ? "SALVEAZĂ ELEV" : "ADAUGĂ ELEV"}
                                                        </CustomButton>
                                                        <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                                                    </FormFieldBox>
                                                </>
                                            )}
                                        </Grid>
                                    </form>
                                </FormProvider>
                            )}

                        </Box>
                    </BoxContent>
                    {!editMode && (
                        <BoxContent mt={3}>
                            <Box sx={{width: "100%"}}>
                                <Box mb={2} sx={{borderBottom: 1, borderColor: "divider"}}>
                                    <Tabs
                                        value={allowedTabs.includes(currentTab as string) ? currentTab : false}
                                        sx={{
                                            "& .MuiTabs-flexContainer": {
                                                flexWrap: "wrap",
                                            },
                                        }}
                                    >
                                        <Tab
                                            label="Statistici"
                                            value={`/elevi/${id}`}
                                            to={`/elevi/${id}`}
                                            component={Link}
                                        />
                                        <Tab
                                            label="Teste"
                                            value={`/elevi/${id}/teste`}
                                            to={`/elevi/${id}/teste`}
                                            component={Link}
                                        />
                                        {/*<Tab*/}
                                        {/*    label="Plan de lucru"*/}
                                        {/*    value={`/elevi/${id}/plan-lucru`}*/}
                                        {/*    to={`/elevi/${id}/plan-lucru`}*/}
                                        {/*    component={Link}*/}
                                        {/*/>*/}
                                    </Tabs>
                                </Box>
                                {location.pathname === `/elevi/${id}` && (
                                    <DashboardStatistics selectedStudent={id} bgColor="light" pageType="student"/>
                                )}
                                {/*{location.pathname === `/elevi/${id}/plan-lucru` && (*/}
                                {/*    <StudentWorkPlan studentId={id}/>*/}
                                {/*)}*/}

                                {location.pathname === `/elevi/${id}/teste` && (
                                    <TestsHistory fromClassroom={true} pageType="student" tabType="history" pageId={id}/>
                                )}
                            </Box>
                        </BoxContent>
                    )}
                </>
            )}
        </Box>
    );
};

export default Student;
